import React from 'react';
import { flowRight as compose } from 'lodash';
import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import Link from '../../components/Link';
import territoryCatalog from '../../data/colombia-territory.json';
import { buildLocationValue, buildRegisteredTerritories, formatTerritoryName } from '../../helpers/homeSearch';
import s from './Home.css';

const categories = [
  { key: 'fincas', label: 'Fincas', icon: 'farm' },
  { key: 'glamping', label: 'Glampings', icon: 'dome' },
  { key: 'cabanas', label: 'Cabañas', icon: 'cabin' },
  { key: 'romanticas', label: 'Románticas', icon: 'heart' },
  { key: 'familiares', label: 'Familiares', icon: 'family' },
  { key: 'aventura', label: 'Aventura', icon: 'mountain' },
  { key: 'gastronomia', label: 'Gastronomía', icon: 'food' },
  { key: 'bienestar', label: 'Bienestar', icon: 'leaf' },
  { key: 'celebraciones', label: 'Celebraciones', icon: 'spark' },
];

const campaigns = [
  { id: 'territory', eyebrow: 'La Colombia rural te espera', title: 'Un país entero para respirar distinto.', text: 'Descubre fincas, glampings y paisajes creados para volver a conectar.', image: '/images/home/rural-island-3d.webp', imageAlt: 'Finca y glamping en una isla natural', action: 'Explorar alojamientos', url: '/s?mode=stays' },
  { id: 'adventure', eyebrow: 'Naturaleza que se siente', title: 'Cambia el ruido por una nueva aventura.', text: 'Encuentra cascadas, senderos y anfitriones que conocen cada rincón.', image: '/images/home/BannerThree.jpg', imageAlt: 'Paisaje verde de una experiencia rural', action: 'Ver experiencias', url: '/s?mode=experiences' },
  { id: 'people', eyebrow: 'Viajes que acercan', title: 'Los mejores recuerdos siempre tienen compañía.', text: 'Elige planes románticos, familiares o para disfrutar con todo tu equipo.', image: '/images/popularLocation/13f96d1479f69394ae3947663a5b2746.jpeg', imageAlt: 'Grupo de amigos disfrutando una experiencia', action: 'Encontrar un plan', url: '/s?mode=experiences&type=Familiares' },
];

function ProductIcon({ type }) {
  if (type === 'experiences') {
    return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 5c-8.7 0-14.5 6.6-14.5 15.1 0 10.1 7.8 16.9 14.5 22.4 6.7-5.5 14.5-12.3 14.5-22.4C38.5 11.6 32.7 5 24 5Z" /><path d="M24 6v35M14.8 11c3.7 8.8 4.5 19.5 9.2 30m9.2-30c-3.7 8.8-4.5 19.5-9.2 30M19.5 42h9l-1.8 4h-5.4Z" /></svg>;
  }
  return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M7 22.5 24 8l17 14.5V41H7Z" /><path d="M4.5 24.5 24 6l19.5 18.5M13 20h10v21H13m16-13h8v13h-8M8 41h33" /></svg>;
}

function CategoryIcon({ type }) {
  const paths = {
    farm: <><path d="M5 18 16 8l11 10v10H5Z" /><path d="M10 28v-8h5v8m4-5h4m-4-4h4M9 11V6h5v1" /></>,
    dome: <><path d="M4 27C5 15 12 8 16 8s11 7 12 19Z" /><path d="M16 8v19M8 16h16M5 23h22M10 10l12 17m0-17L10 27" /></>,
    cabin: <><path d="m4 16 12-9 12 9-2 2-10-7-10 7Z" /><path d="M7 17v11h18V17M11 28v-8h6v8m4-8h2" /></>,
    heart: <path d="M16 27S5 21 5 13.5C5 9 10.5 7 16 12c5.5-5 11-3 11 1.5C27 21 16 27 16 27Z" />,
    family: <><circle cx="11" cy="11" r="4" /><circle cx="22" cy="12" r="3" /><path d="M4 27c.5-7 3-10 7-10s6.5 3 7 10m0-1c.3-5 1.8-8 4.5-8 3 0 4.8 3 5 8" /></>,
    mountain: <><path d="m3 27 9-16 5 8 3-5 9 13Z" /><path d="m9 17 3 2 3-3m3 5 2 1 3-3" /></>,
    food: <><path d="M8 5v10m4-10v10m-7-5h10m-5 5v12M23 5c-4 4-4 9 0 12v10m0-10c4-2 5-8 2-12" /></>,
    leaf: <><path d="M27 5C15 6 7 11 7 20c0 5 4 8 9 7 9-2 11-12 11-22Z" /><path d="M6 29c4-7 9-12 17-18" /></>,
    spark: <><path d="M16 3c1 8 5 12 13 13-8 1-12 5-13 13-1-8-5-12-13-13 8-1 12-5 13-13Z" /><path d="M26 3c.3 3 1.7 4.7 5 5-3.3.3-4.7 2-5 5-.3-3-1.7-4.7-5-5 3.3-.3 4.7-2 5-5Z" /></>,
  };
  return <svg viewBox="0 0 32 32" aria-hidden="true">{paths[type]}</svg>;
}

function ProductTabs({ mode, onChange }) {
  return <nav className={s.productTabs} aria-label="Tipos de viaje">
    <button type="button" className={classNames(s.productTab, { [s.productTabActive]: mode === 'stays' })} onClick={() => onChange('stays')} aria-pressed={mode === 'stays'}><b>Alojamientos</b><span><CategoryIcon type="leaf" /></span></button>
    <button type="button" className={classNames(s.productTab, { [s.productTabActive]: mode === 'experiences' })} onClick={() => onChange('experiences')} aria-pressed={mode === 'experiences'}><b>Experiencias <i className={s.desktopLabel}>rurales</i></b><span><CategoryIcon type="leaf" /></span></button>
  </nav>;
}

class LocationSelectors extends React.Component {
  constructor(props) {
    super(props);
    this.state = { state: '', city: '' };
    this.selectState = this.selectState.bind(this);
  }

  selectState(event) {
    this.setState({ state: event.target.value, city: '' });
  }

  render() {
    const { locations, isExperience } = this.props;
    const { state, city } = this.state;
    const departments = buildRegisteredTerritories(locations, territoryCatalog);
    const selectedDepartment = departments.find(item => item.name === state);
    const cities = selectedDepartment ? selectedDepartment.municipalities : [];
    const locationValue = buildLocationValue(city, state);

    return <div className={classNames(s.locationField, s.locationSelectors)}>
      <div className={s.locationSelects}>
        <label><span>País</span><span className={s.countryName}><i className={s.colombiaFlag} aria-hidden="true" />Colombia</span><input type="hidden" name="country" value="CO" /></label>
        <label><span>Departamento</span><select name="state" value={state} onChange={this.selectState} aria-label="Departamento"><option value="">Todos los departamentos</option>{departments.map(item => <option key={item.code} value={item.name}>{formatTerritoryName(item.name)}</option>)}</select></label>
        <label><span>Ciudad o municipio</span><select name="city" value={city} onChange={event => this.setState({ city: event.target.value })} aria-label="Ciudad o municipio" disabled={!state}><option value="">{state ? 'Todos los municipios' : 'Seleccione un departamento'}</option>{cities.map(item => <option key={item.code} value={item.name}>{formatTerritoryName(item.name)}</option>)}</select></label>
      </div>
      {!departments.length && <p className={s.locationNotice}>Aún no hay {isExperience ? 'experiencias' : 'alojamientos'} publicados para filtrar por ubicación.</p>}
      <input type="hidden" name="address" value={locationValue} />
    </div>;
  }
}

function SearchBar({ mode, searchLocations }) {
  const isExperience = mode === 'experiences';
  const today = new Date().toISOString().slice(0, 10);
  const validateDates = event => {
    const form = event.currentTarget;
    const startDate = form.elements.startdate.value;
    const endDate = form.elements.enddate.value;
    const endField = form.elements.enddate;
    endField.setCustomValidity('');
    if (startDate && endDate && endDate <= startDate) {
      event.preventDefault();
      endField.setCustomValidity('La fecha de salida debe ser posterior a la fecha de llegada.');
      endField.reportValidity();
    }
  };
  const updateEndDateMinimum = event => {
    event.currentTarget.form.elements.enddate.min = event.currentTarget.value || today;
  };
  return <form className={classNames(s.searchBar, { [s.searchBarExperience]: isExperience })} action="/s" method="get" onSubmit={validateDates}>
    <input type="hidden" name="mode" value={mode} />
    <LocationSelectors isExperience={isExperience} locations={(searchLocations && searchLocations[mode]) || []} />
    <label><span>{isExperience ? 'Fecha' : 'Llegada'}</span><input name="startdate" type="date" min={today} onChange={updateEndDateMinimum} aria-label={isExperience ? 'Fecha de la experiencia' : 'Fecha de llegada'} /></label>
    <label><span>{isExperience ? 'Hasta' : 'Salida'}</span><input name="enddate" type="date" min={today} aria-label={isExperience ? 'Fecha final de la experiencia' : 'Fecha de salida'} /></label>
    <label className={s.peopleField}><span>{isExperience ? 'Participantes' : 'Quién'}</span><select name="guests" defaultValue="" aria-label={isExperience ? 'Número de participantes' : 'Número de huéspedes'}><option value="">Cualquier cantidad</option><option value="1">1 {isExperience ? 'participante' : 'persona'}</option><option value="2">2 {isExperience ? 'participantes' : 'personas'}</option><option value="3">3 {isExperience ? 'participantes' : 'personas'}</option><option value="4">4 {isExperience ? 'participantes' : 'personas'}</option><option value="6">5 o más {isExperience ? 'participantes' : 'personas'}</option></select></label>
    <button className={s.searchButton} type="submit" aria-label={isExperience ? 'Buscar experiencias rurales' : 'Buscar alojamientos'}><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.8" cy="10.8" r="6.8" /><path d="m16 16 4 4" /></svg><span>Buscar</span></button>
  </form>;
}

function EditorialHero({ searchMode, onModeChange, searchLocations }) {
  return <section className={s.editorialHero}>
    <section className={s.discoveryHeader} aria-label="Buscador de alojamientos y experiencias rurales">
      <ProductTabs mode={searchMode} onChange={onModeChange} />
    </section>
    <div className={s.editorialHeroContent}>
      <p>Turismo rural con identidad</p>
      <h1>Vive experiencias<br />que no se olvidan</h1>
      <span>Actividades auténticas en el campo colombiano</span>
    </div>
    <SearchBar key={searchMode} mode={searchMode} searchLocations={searchLocations} />
  </section>;
}

function ListingCard({ item, favorite, onFavorite }) {
  const url = item.url || `/s?address=${encodeURIComponent(item.place)}`;
  return <article className={s.listingCard}>
    <div className={classNames(s.cardImage, { [s.cardImageContain]: item.imageContain })}>
      <Link to={url} aria-label={`Ver ${item.detail} en ${item.place}`}>{item.image ? <img src={item.image} alt={`${item.detail} en ${item.place}`} loading="lazy" /> : <span>Foto no disponible</span>}</Link>
      {item.badge && <span className={s.cardBadge}>{item.badge}</span>}
      <button type="button" className={classNames(s.favoriteButton, { [s.favoriteActive]: favorite })} onClick={() => onFavorite(item.id)} aria-label={favorite ? `Quitar ${item.detail} de favoritos` : `Guardar ${item.detail} en favoritos`} aria-pressed={favorite}><svg viewBox="0 0 32 32" aria-hidden="true"><path d="M16 27S5 21 5 13.5C5 9 10.5 7 16 12c5.5-5 11-3 11 1.5C27 21 16 27 16 27Z" /></svg></button>
    </div>
    <Link className={s.cardBody} to={url}>
      <div className={s.cardTitle}><h3>{item.place}</h3><span><b aria-hidden="true">★</b> {item.rating}</span></div>
      <p>{item.detail}</p><strong>{item.price}</strong>
    </Link>
  </article>;
}

function ListingSection({ title, subtitle, items, favorites, onFavorite, actionUrl }) {
  if (!items.length) return null;
  return <section className={s.listingSection}>
    <header className={s.sectionHeader}><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div><Link className={s.sectionAction} to={actionUrl || '/s'} aria-label={`Ver todos: ${title}`}>Ver todos <span aria-hidden="true">→</span></Link></header>
    <div className={s.cardTrack}>{items.map(item => <ListingCard key={item.id} item={item} favorite={Boolean(favorites[item.id])} onFavorite={onFavorite} />)}</div>
  </section>;
}

function PromoCarousel({ campaign, activeIndex, onSelect }) {
  return <section className={classNames(s.promo, s[`promo${campaign.id}`])} aria-roledescription="carrusel" aria-label="Ideas para tu próxima escapada">
    <div className={s.promoCopy} key={`${campaign.id}-copy`}><p>{campaign.eyebrow}</p><h2>{campaign.title}</h2><span>{campaign.text}</span><Link to={campaign.url}>{campaign.action} <b aria-hidden="true">→</b></Link></div>
    <div className={s.promoVisual} key={`${campaign.id}-image`}><img src={campaign.image} alt={campaign.imageAlt} className={campaign.id === 'territory' ? s.promoImageContain : ''} /></div>
    <div className={s.promoDots}>{campaigns.map((item, index) => <button type="button" key={item.id} onClick={() => onSelect(index)} className={classNames({ [s.promoDotActive]: activeIndex === index })} aria-label={`Mostrar promoción ${index + 1}`} aria-current={activeIndex === index ? 'true' : undefined}><i /></button>)}</div>
  </section>;
}

class Home extends React.Component {
  constructor(props) {
    super(props);
    this.state = { searchMode: 'stays', activeCategory: 'fincas', campaignIndex: 0, favorites: {} };
    this.campaignTimer = null;
    this.selectMode = this.selectMode.bind(this);
    this.selectCampaign = this.selectCampaign.bind(this);
    this.toggleFavorite = this.toggleFavorite.bind(this);
  }

  componentDidMount() {
    this.startCampaignTimer();
  }

  componentWillUnmount() {
    if (this.campaignTimer) window.clearInterval(this.campaignTimer);
  }

  startCampaignTimer() {
    if (this.campaignTimer) window.clearInterval(this.campaignTimer);
    this.campaignTimer = window.setInterval(() => this.setState(state => ({ campaignIndex: (state.campaignIndex + 1) % campaigns.length })), 5000);
  }

  selectMode(searchMode) {
    this.setState({ searchMode });
  }

  selectCampaign(campaignIndex) {
    this.setState({ campaignIndex });
    this.startCampaignTimer();
  }

  toggleFavorite(id) {
    this.setState(state => ({ favorites: Object.assign({}, state.favorites, { [id]: !state.favorites[id] }) }));
  }

  render() {
    const { searchMode, activeCategory, campaignIndex, favorites } = this.state;
    const experienceMode = searchMode === 'experiences';
    const dynamicExperiences = this.props.publishedExperiences || [];
    const publishedStays = this.props.publishedStays || [];
    const firstSection = experienceMode ? dynamicExperiences : publishedStays;
    const nearby = experienceMode ? publishedStays : dynamicExperiences.filter(item => item.state.toLocaleUpperCase('es-CO') === 'ANTIOQUIA');

    return <div className={s.root}>
      <div className={s.heroScene}>
      <EditorialHero searchMode={searchMode} onModeChange={this.selectMode} searchLocations={this.props.searchLocations} />
      <nav className={s.categoryNav} aria-label="Categorías rurales">
        <div className={s.categoryTrack}>{categories.map(category => <Link key={category.key} to={`/s?type=${encodeURIComponent(category.label)}`} className={classNames(s.categoryItem, { [s.categoryItemActive]: activeCategory === category.key })} onClick={() => this.setState({ activeCategory: category.key })} aria-current={activeCategory === category.key ? 'page' : undefined}><CategoryIcon type={category.icon} /><span>{category.label}</span></Link>)}</div>
        <Link className={s.filterButton} to="/s"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M7 12h10M10 17h4" /></svg><span>Filtros</span></Link>
      </nav>
      </div>
      <main className={s.content}>
        <ListingSection title={experienceMode ? 'Completa tu viaje con un lugar especial' : 'Experiencias cerca de Medellín'} subtitle={experienceMode ? 'Descansa cerca de la experiencia que quieres vivir.' : 'Naturaleza y descanso a pocas horas de la ciudad.'} items={nearby} favorites={favorites} onFavorite={this.toggleFavorite} actionUrl={experienceMode ? '/s?mode=stays&country=CO' : '/s?mode=experiences&country=CO&state=Antioquia'} />
        <ListingSection title={experienceMode ? 'Experiencias rurales que dejan huella' : 'Alojamientos favoritos entre viajeros'} subtitle={experienceMode ? 'Planes únicos guiados por personas que conocen el territorio.' : 'Los lugares mejor valorados por nuestra comunidad.'} items={firstSection} favorites={favorites} onFavorite={this.toggleFavorite} actionUrl={experienceMode ? '/s?mode=experiences' : '/s?mode=stays'} />
        <PromoCarousel campaign={campaigns[campaignIndex]} activeIndex={campaignIndex} onSelect={this.selectCampaign} />
        {!experienceMode && <ListingSection title="Experiencias rurales para recordar" subtitle="Sabores, naturaleza y aventura de la mano de anfitriones locales." items={dynamicExperiences} favorites={favorites} onFavorite={this.toggleFavorite} actionUrl="/s?mode=experiences" />}
        <section className={s.inspiration}>
          <div className={s.sectionHeader}><div><h2>Inspírate para tu próxima escapada</h2><p>Destinos rurales para cada tipo de viaje.</p></div></div>
          <div className={s.destinationGrid}><Link to="/s?address=Antioquia"><b>Antioquia</b><span>Montañas, embalses y tradición</span></Link><Link to="/s?address=Quindio"><b>Eje Cafetero</b><span>Café, fincas y paisajes verdes</span></Link><Link to="/s?address=Santander"><b>Santander</b><span>Aventura, historia y cañones</span></Link><Link to="/s?address=Boyaca"><b>Boyacá</b><span>Pueblos, lagunas y descanso</span></Link></div>
        </section>
        <section className={s.hostBanner}>
          <div><p>Comparte lo mejor de tu territorio</p><h2>Convierte tu lugar o tu conocimiento en una experiencia inolvidable.</h2><span>Publica, administra disponibilidad y recibe reservas desde un solo espacio.</span><div className={s.hostActions}><Link to="/become-a-host/experience">Registrar experiencia <b aria-hidden="true">→</b></Link><Link to="/become-a-host?mode=new" className={s.hostStayAction}>Registrar alojamiento <b aria-hidden="true">→</b></Link></div></div>
          <div className={s.hostArt}><span className={s.hostHouse}><i /><b /></span><span className={s.hostSun} /><span className={s.hostHillOne} /><span className={s.hostHillTwo} /></div>
        </section>
      </main>
    </div>;
  }
}

export default compose(withStyles(s))(Home);

